<script setup lang="ts">
import {ref, computed, onMounted, onBeforeUnmount, watch, shallowRef, inject} from 'vue';
import {useQuasar} from 'quasar';
import {echarts} from '@/utils/common/echartsSetup.ts';
import type {ECElementEvent} from 'echarts/core';
import {DashboardWidgetProps, dashboardPresenterKey} from "@/utils/presenters/interfaces.ts";
import DashboardUtils from "@/utils/common/DashboardUtils.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import {useDashboardViewOptionsStore} from "@/stores/dashboardViewOptionsStore.ts";
import Translator from "@/utils/common/Translator.ts";
import type {
  BIEChartsOption,
  BIClickEmitMappingData,
  BIClickEmitPoint,
  BIDrillDownClick,
  BIDrillDownByClick,
  BIDrillDownByDimension,
  BIDrillDownData,
  BIDrillDownFilterMetadata,
  BIDrillDownPointValues,
  BIDrillDownTarget,
  BIFormatSpec,
  BILinkData,
  BILinkTarget,
  BILinkPointValues,
  CrossFilterEmission,
} from "@/components/bi/biChartTypes.ts";
import BIActionMenu from "@/components/bi/BIActionMenu.vue";
import WizardSlotSelector from "@/components/bi/WizardSlotSelector.vue";
import {buildDrillDownClick, buildDrillDownByClick, executeLink} from "@/components/bi/biInteractionHelpers.ts";
import {useBIFilterBadges} from "@/components/bi/useBIFilterBadges.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import DTOBIChartLine = namaTypes.DTOBIChartLine;

interface BITooltipFormatterParam {
  seriesIndex?: number;
  value: number | string;
  marker?: string;
  seriesName?: string;
  name?: string;
  percent?: number;
  axisValueLabel?: string;
}

interface BIAxisOption {
  type?: 'value' | 'category' | 'time' | 'log';
  axisLabel?: {formatter?: (value: number) => string};
}

interface BIGaugeSeries {
  type?: string;
  detail?: {formatter?: (value: number) => string};
}

interface BILabelFormatterParam {
  value: number | string | unknown[];
  seriesIndex?: number;
  dataIndex?: number;
}

interface BISeriesWithLabel {
  type?: string;
  label?: {show?: boolean; formatter?: unknown};
}

type NumberFormatter = (value: number) => string;

const props = defineProps<DashboardWidgetProps>();
const emit = defineEmits<{
  crossFilterClicked: [emissions: CrossFilterEmission[]],
  drillDownRequested: [click: BIDrillDownClick],
  drillDownByRequested: [click: BIDrillDownByClick]
}>();

interface DrillDownMenuState {
  targets: BIDrillDownTarget[];
  pointValues: BIDrillDownPointValues;
  filterMetadata: Record<string, BIDrillDownFilterMetadata>;
  drillDownByDimensions: BIDrillDownByDimension[];
  drillDownByDataIndex?: number;
  linkTargets: BILinkTarget[];
  linkPointValues: BILinkPointValues;
  sourceLabel?: string;
  x: number;
  y: number;
}

const drillDownMenu = ref<DrillDownMenuState | undefined>();
const drillDownMenuOpen = computed<boolean>({
  get: () => !!drillDownMenu.value,
  set: (v) => { if (!v) drillDownMenu.value = undefined; },
});

const {darkMode} = storeToRefs(useNamaFlagsStore());
const crossFilterStore = useCrossFilterStore();
const viewOptionsStore = useDashboardViewOptionsStore();
const dashboardPresenter = inject(dashboardPresenterKey, undefined);
const $q = useQuasar();
const chartRef = ref<HTMLElement>();
const chartInstance = shallowRef<echarts.ECharts>();
const resizeObserver = shallowRef<ResizeObserver>();
const errorMessage = ref<string>();
const runtimeSlotsDialogOpen = ref(false);

async function onSlotOverrideChanged() {
  if (widgetId.value)
    await dashboardPresenter?.redrawWidgets(widgetId.value);
}

const biChartLine = computed<DTOBIChartLine>(() => props.chartLine as DTOBIChartLine);
const widgetId = computed<string | undefined>(() => biChartLine.value?.widgetId);
const appliedCrossFilterCodes = computed<string[]>(() => biChartLine.value?.appliedCrossFilterCodes ?? []);
const {isFilterSource, isFilteredByOther, filteredByTooltip, emittingTooltip} =
  useBIFilterBadges(widgetId, appliedCrossFilterCodes);

onMounted(() => {
  if (!chartRef.value) return;
  initChart();

  resizeObserver.value = new ResizeObserver(() => chartInstance.value?.resize());
  resizeObserver.value.observe(chartRef.value);

  chartRef.value.addEventListener('contextmenu', handleNativeContextMenu);
});

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect();
  disposeChart();
  chartRef.value?.removeEventListener('contextmenu', handleNativeContextMenu);
});

function disposeChart() {
  const chart = chartInstance.value;
  if (!chart || chart.isDisposed()) return;
  chart.getZr().off('click', handleUnclaimedZrClick);
  chart.dispose();
}

function handleNativeContextMenu(e: MouseEvent) {
  const echartOption = getEchartOption();
  if (echartOption?._drillDownData?.targets?.length || echartOption?._drillDownByData?.dimensions?.length || echartOption?._linkData?.links?.length)
    e.preventDefault();
}

watch(() => props.chartKey, () => {
  applyOption();
});

watch(() => biChartLine.value?.echartOption, () => {
  applyOption();
});

watch(() => viewOptionsStore.showToolbox, () => {
  applyOption();
});

watch(darkMode, () => {
  disposeChart();
  if (chartRef.value) {
    initChart();
  }
});

// Set by the ECharts-level click when it carried a dataIndex. zrender calls our zr listener BEFORE
// ECharts' own (ECharts registers its handler with callAtLast), so the fallback defers its check to a
// microtask that runs after the whole synchronous zr dispatch, and never double-dispatches.
let clickHandledByECharts = false;

function initChart() {
  chartInstance.value = echarts.init(chartRef.value!, darkMode.value ? 'dark' : undefined, {
    renderer: 'canvas'
  });
  if (darkMode.value) {
    chartInstance.value.getZr().painter?.getViewportRoot()?.style?.setProperty('background', 'transparent');
  }
  applyOption();

  chartInstance.value.on('click', (params: ECElementEvent) => {
    clickHandledByECharts = params.dataIndex !== undefined;
    handleChartClick(params);
  });
  chartInstance.value.getZr().on('click', handleUnclaimedZrClick);

  chartInstance.value.on('contextmenu', (params: ECElementEvent) => {
    const echartOption = getEchartOption();
    const drillDown = echartOption?._drillDownData;
    const drillDownBy = echartOption?._drillDownByData;
    const linkData = echartOption?._linkData;
    const hasDrillTargets = !!drillDown?.targets?.length;
    const hasDrillByDims = !!drillDownBy?.dimensions?.length;
    const hasLinks = !!linkData?.links?.length;
    const pointIndex = normalisePointIndex(params);
    if ((!hasDrillTargets && !hasDrillByDims && !hasLinks) || pointIndex === undefined) return;

    const nativeEvent = (params.event as unknown as {event: MouseEvent} | undefined)?.event;
    nativeEvent?.preventDefault?.();

    let pointValues: BIDrillDownPointValues | undefined;
    if (hasDrillTargets) {
      pointValues = lookupDrillDownPointValues(drillDown!, params.seriesIndex, pointIndex);
      if (!pointValues || Object.keys(pointValues).length === 0 || (pointValues as {isOthers?: boolean}).isOthers)
        pointValues = undefined;
    }

    let linkPointValues: BILinkPointValues = {};
    if (hasLinks) {
      const looked = lookupLinkPointValues(linkData!, params.seriesIndex, pointIndex);
      if (!(looked as {isOthers?: boolean} | undefined)?.isOthers) linkPointValues = looked ?? {};
    }

    const drillByPointIsOthers = !!(hasDrillByDims && drillDownBy!.points?.[pointIndex]?.isOthers);
    const effectiveHasDrillByDims = hasDrillByDims && !drillByPointIsOthers;

    if (!pointValues && !effectiveHasDrillByDims && !Object.keys(linkPointValues).length) return;

    const label = buildDrillDownSourceLabel(params);
    drillDownMenu.value = {
      targets: drillDown?.targets ?? [],
      pointValues: pointValues ?? {},
      filterMetadata: drillDown?.filterMetadata ?? {},
      sourceLabel: label,
      drillDownByDimensions: effectiveHasDrillByDims ? (drillDownBy?.dimensions ?? []) : [],
      drillDownByDataIndex: pointIndex,
      linkTargets: linkData?.links ?? [],
      linkPointValues,
      x: nativeEvent?.clientX ?? 0,
      y: nativeEvent?.clientY ?? 0,
    };
  });
}

function handleChartClick(params: ECElementEvent) {
  const echartOption = getEchartOption();
  const clickAction = echartOption?._clickAction;
  const pointIndex = normalisePointIndex(params);

  if (clickAction?.type === 'link' && clickAction.targetKey && pointIndex !== undefined) {
    const linkData = echartOption?._linkData;
    const targetLink = linkData?.links?.find(l => l.key === clickAction.targetKey);
    if (targetLink) {
      const pointValues = lookupLinkPointValues(linkData!, params.seriesIndex, pointIndex);
      const pv = pointValues?.[targetLink.key];
      if (pv) executeLink(targetLink, pv);
      return;
    }
  }

  if (clickAction?.type === 'drillDown' && clickAction.targetKey && pointIndex !== undefined) {
    const drillDown = echartOption?._drillDownData;
    const target = drillDown?.targets?.find(t => t.key === clickAction.targetKey);
    if (target && drillDown) {
      const pointValues = lookupDrillDownPointValues(drillDown, params.seriesIndex, pointIndex);
      if (pointValues) {
        const state: DrillDownMenuState = {
          targets: drillDown.targets,
          pointValues,
          filterMetadata: drillDown.filterMetadata ?? {},
          drillDownByDimensions: [],
          linkTargets: [],
          linkPointValues: {},
          sourceLabel: buildDrillDownSourceLabel(params),
          x: 0, y: 0,
        };
        drillDownMenu.value = state;
        selectDrillDownTarget(target);
      }
      return;
    }
  }

  // Default: cross-filter emission (current behavior when clickAction absent or type === 'crossFilter')
  const clickEmitData = echartOption?._clickEmitData;
  if (clickEmitData?.length && pointIndex !== undefined) {
    const emissions: CrossFilterEmission[] = [];
    for (const mapping of clickEmitData) {
      const point = lookupClickEmitPoint(mapping, params.seriesIndex, pointIndex);
      if (point?.isOthers) continue;
      if (point && Object.keys(point).length > 0)
        emissions.push({
          crossFilterCode: mapping.crossFilterCode,
          sourceChartId: mapping.sourceChartId ?? widgetId.value,
          seriesIndex: params.seriesIndex,
          // ECharts-native index: the store replays it through dispatchAction('select').
          dataIndex: params.dataIndex,
          ...point
        });
    }
    if (emissions.length) {
      emit('crossFilterClicked', emissions);
      return;
    }
  }
  DashboardUtils.handleChartClick(params);
}

// Overlays such as a line's areaStyle polygon swallow the pointer event without carrying a
// dataIndex, which made bars underneath (and the empty plot area) unclickable. When the ECharts
// click did not claim the event, map the pixel back to the nearest category on series 0 and
// dispatch it exactly like a normal click.
function handleUnclaimedZrClick(e: {offsetX: number; offsetY: number}) {
  const {offsetX, offsetY} = e;
  queueMicrotask(() => {
    const handled = clickHandledByECharts;
    clickHandledByECharts = false;
    if (handled) return;
    const params = resolveCategoryClickFromPixel(offsetX, offsetY);
    if (params) handleChartClick(params);
  });
}

// Heatmap points are (x, y) cells, so an x-category index is not a point index.
const PIXEL_FALLBACK_EXCLUDED_SERIES = new Set(['heatmap']);

function resolveCategoryClickFromPixel(x: number, y: number): ECElementEvent | undefined {
  const chart = chartInstance.value;
  const option = getEchartOption();
  if (!chart || chart.isDisposed() || !option || !isCartesianChart(option)) return undefined;
  if (PIXEL_FALLBACK_EXCLUDED_SERIES.has(seriesAt(option, 0)?.type ?? '')) return undefined;
  const categoryAxis = findCategoryAxis(option);
  if (!categoryAxis) return undefined;
  const finder = {seriesIndex: 0};
  if (!chart.containPixel(finder, [x, y])) return undefined;
  const coords = chart.convertFromPixel(finder, [x, y]);
  const raw = coords?.[categoryAxis.coordIndex];
  if (typeof raw !== 'number' || !isFinite(raw)) return undefined;
  const dataIndex = Math.round(raw);
  if (dataIndex < 0 || dataIndex >= categoryAxis.length) return undefined;
  const firstSeries = seriesAt(option, 0);
  return {
    componentType: 'series',
    seriesType: firstSeries?.type,
    seriesIndex: 0,
    seriesName: firstSeries?.name,
    dataIndex,
    name: categoryAxis.categories[dataIndex],
  } as unknown as ECElementEvent;
}

interface BICategoryAxisInfo {
  coordIndex: 0 | 1;
  categories: unknown[];
  length: number;
}

function findCategoryAxis(option: BIEChartsOption): BICategoryAxisInfo | undefined {
  const firstCategoryAxis = (axis: unknown): {data?: unknown[]} | undefined => {
    const a = (Array.isArray(axis) ? axis[0] : axis) as {type?: string; data?: unknown[]} | undefined;
    if (!a) return undefined;
    const isCategory = a.type === 'category' || (!a.type && Array.isArray(a.data));
    return isCategory ? a : undefined;
  };
  const seriesLength = (seriesAt(option, 0)?.data as unknown[] | undefined)?.length ?? 0;
  const describe = (axis: {data?: unknown[]}, coordIndex: 0 | 1): BICategoryAxisInfo | undefined => {
    const categories = axis.data ?? [];
    const length = categories.length || seriesLength;
    return length > 0 ? {coordIndex, categories, length} : undefined;
  };
  const xAxis = firstCategoryAxis((option as any).xAxis);
  if (xAxis) return describe(xAxis, 0);
  const yAxis = firstCategoryAxis((option as any).yAxis);
  if (yAxis) return describe(yAxis, 1);
  return undefined;
}

function seriesAt(option: BIEChartsOption | undefined, seriesIndex: number | undefined):
  {type?: string; name?: string; data?: unknown} | undefined {
  const series = option?.series;
  if (!series) return undefined;
  const list = Array.isArray(series) ? series : [series];
  return list[seriesIndex ?? 0] as {type?: string; name?: string; data?: unknown} | undefined;
}

// ECharts synthesises a virtual root node for treemaps, so a leaf's dataIndex starts at 1 while
// the backend point arrays are flat and zero-based. Treemap nodes are resolved by name against
// the series data (emitted in the same order as the points); every other series type keeps the
// raw dataIndex.
function normalisePointIndex(params: ECElementEvent): number | undefined {
  if (params.dataIndex === undefined) return undefined;
  const series = seriesAt(getEchartOption(), params.seriesIndex);
  if (series?.type !== 'treemap') return params.dataIndex;
  const name = params.name || (params.data as {name?: string} | undefined)?.name;
  const data = Array.isArray(series.data) ? series.data as {name?: string}[] : [];
  const byName = name ? data.findIndex(d => d?.name === name) : -1;
  return byName >= 0 ? byName : params.dataIndex - 1;
}

function lookupDrillDownPointValues(drillDown: BIDrillDownData, seriesIndex: number | undefined, dataIndex: number): BIDrillDownPointValues | undefined {
  if (drillDown.pointsBySeries && seriesIndex !== undefined)
    return drillDown.pointsBySeries[seriesIndex]?.[dataIndex];
  return drillDown.points?.[dataIndex];
}

function lookupLinkPointValues(linkData: BILinkData, seriesIndex: number | undefined, dataIndex: number): BILinkPointValues | undefined {
  if (linkData.pointsBySeries && seriesIndex !== undefined)
    return linkData.pointsBySeries[seriesIndex]?.[dataIndex];
  return linkData.points?.[dataIndex];
}

function selectLinkTarget(linkTarget: BILinkTarget) {
  const state = drillDownMenu.value;
  if (!state) return;
  const pointValue = state.linkPointValues[linkTarget.key];
  if (pointValue) executeLink(linkTarget, pointValue);
  drillDownMenu.value = undefined;
}

function buildDrillDownSourceLabel(params: ECElementEvent): string {
  const parts: string[] = [];
  const name = (params as unknown as {name?: string}).name;
  if (name) parts.push(name);
  const seriesName = (params as unknown as {seriesName?: string}).seriesName;
  if (seriesName && seriesName !== name) parts.push(seriesName);
  return parts.join(' / ');
}

function selectDrillDownTarget(target: BIDrillDownTarget) {
  const state = drillDownMenu.value;
  if (!state) return;
  const sourceWidgetCode = (biChartLine.value as unknown as {widgetCode?: string} | undefined)?.widgetCode;
  const click = buildDrillDownClick(target, state.pointValues, state.filterMetadata,
    sourceWidgetCode, widgetId.value, state.sourceLabel);
  if (click) emit('drillDownRequested', click);
  drillDownMenu.value = undefined;
}

function selectDrillDownByDimension(dimension: BIDrillDownByDimension) {
  const state = drillDownMenu.value;
  if (!state) return;
  const sourceWidgetCode = (biChartLine.value as unknown as {widgetCode?: string} | undefined)?.widgetCode;
  const click = buildDrillDownByClick(dimension, getEchartOption()?._drillDownByData,
    state.drillDownByDataIndex, sourceWidgetCode, widgetId.value, props.dashboardId);
  if (click) emit('drillDownByRequested', click);
  drillDownMenu.value = undefined;
}

function lookupClickEmitPoint(mapping: BIClickEmitMappingData, seriesIndex: number | undefined, dataIndex: number): BIClickEmitPoint | undefined {
  if (mapping.pointsBySeries && seriesIndex !== undefined)
    return mapping.pointsBySeries[seriesIndex]?.[dataIndex];
  return mapping.points?.[dataIndex];
}

function getEchartOption(): BIEChartsOption | undefined {
  return biChartLine.value.echartOption as BIEChartsOption | undefined;
}

function applyOption() {
  if (!chartInstance.value) return;

  const echartOption = getEchartOption();
  const error = biChartLine.value.errorMessage;

  if (error) {
    errorMessage.value = error;
    return;
  }

  if (!echartOption) {
    errorMessage.value = Translator.translate('biNoChartConfiguration');
    return;
  }

  errorMessage.value = undefined;
  try {
    if (!echartOption.backgroundColor) {
      echartOption.backgroundColor = 'transparent';
    }
    applyFormatters(echartOption);
    injectSelectionStyle(echartOption);
    injectToolbox(echartOption);
    injectDataZoom(echartOption);
    injectTempoTooltip(echartOption);
    chartInstance.value.setOption(echartOption, {notMerge: true});
    applyEmitHighlight();
  } catch (e) {
    errorMessage.value = Translator.translate('biChartConfigurationError');
  }
}

function injectSelectionStyle(option: BIEChartsOption) {
  const series = option.series;
  if (!series) return;
  const seriesArray = Array.isArray(series) ? series : [series];
  for (const s of seriesArray) {
    const sAny = s as any;
    if (sAny.type === 'gauge' || sAny.type === 'heatmap') continue;
    sAny.selectedMode = 'single';
    sAny.select = sAny.select ?? {};
    sAny.select.itemStyle = {
      borderColor: '#ff9800',
      borderWidth: 3,
      shadowColor: 'rgba(255, 152, 0, 0.6)',
      shadowBlur: 14,
      ...(sAny.select.itemStyle ?? {}),
    };
  }
}

function isCartesianChart(option: BIEChartsOption): boolean {
  return !!(option as any).xAxis || !!(option as any).yAxis;
}

function hasMultipleSeries(option: BIEChartsOption): boolean {
  const series = (option as any).series;
  return Array.isArray(series) && series.length > 1;
}

function injectToolbox(option: BIEChartsOption) {
  if (!viewOptionsStore.showToolbox) {
    delete (option as any).toolbox;
    return;
  }
  const svgIcon = (svg: string) => 'image://data:image/svg+xml,' + encodeURIComponent(svg);
  const feature: Record<string, any> = {
    myCollapseWidget: {
      show: true,
      title: Translator.translate('biCollapseWidget'),
      icon: svgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>'),
      onclick: () => viewOptionsStore.toggleCollapsed(widgetId.value),
    },
    saveAsImage: {title: Translator.translate('biSaveAsImage')},
    dataView: {title: Translator.translate('biDataView'), readOnly: true, lang: [
      Translator.translate('biDataView'),
      Translator.translate('biClose'),
      Translator.translate('biRefresh'),
    ]},
  };

  if (biChartLine.value?.runtimeSelectorInfo) {
    feature.myRuntimeSlots = {
      show: true,
      title: Translator.translate('biRuntimeSlotSelection'),
      icon: svgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>'),
      onclick: () => { runtimeSlotsDialogOpen.value = true; },
    };
  }

  if (isAdmin.value) {
    feature.myExportOption = {
      show: true,
      title: 'Copy EChart Option',
      icon: svgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>'),
      onclick: () => exportEChartOption(),
    };
    feature.myOpenWidget = {
      show: true,
      title: 'Open Widget',
      icon: svgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>'),
      onclick: () => openWidgetEditView(),
    };
    feature.myCopyServerResponse = {
      show: true,
      title: 'Copy Server Response',
      icon: svgIcon('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>'),
      onclick: () => copyServerResponse(),
    };
  }

  if (isCartesianChart(option)) {
    const magicTypes: string[] = ['line', 'bar'];
    const magicTitles: Record<string, string> = {
      line: Translator.translate('biLineChart'),
      bar: Translator.translate('biBarChart'),
    };
    if (hasMultipleSeries(option)) {
      magicTypes.push('stack');
      magicTitles.stack = Translator.translate('biStackChart');
    }
    feature.magicType = {type: magicTypes, title: magicTitles};
    feature.restore = {title: Translator.translate('biRestore')};
  }

  (option as any).toolbox = {
    show: true,
    right: 10,
    top: 0,
    feature,
    iconStyle: {borderColor: '#999'},
    emphasis: {iconStyle: {borderColor: '#666'}},
  };
}

function hasTimeAxis(option: BIEChartsOption): boolean {
  const check = (axis: unknown) => {
    if (!axis) return false;
    const axes = Array.isArray(axis) ? axis : [axis];
    return axes.some((a: any) => a.type === 'time');
  };
  return check((option as any).xAxis) || check((option as any).yAxis);
}

function injectDataZoom(option: BIEChartsOption) {
  if ((option as any).dataZoom) return;
  if (!hasTimeAxis(option)) return;
  (option as any).dataZoom = [
    {type: 'slider', show: true, start: 0, end: 100, height: 20, bottom: 5},
    {type: 'inside', start: 0, end: 100},
  ];
}

function injectTempoTooltip(option: BIEChartsOption) {
  const tooltipData = (option as any)._tooltipData as {arabic?: string[]; english?: string[]} | undefined;
  if (!tooltipData) return;
  const htmlArray = Translator.isArabic() ? tooltipData.arabic : tooltipData.english;
  if (!htmlArray?.length) return;
  const tooltip = (option as any).tooltip ?? {};
  (option as any).tooltip = tooltip;
  tooltip.formatter = (params: BITooltipFormatterParam | BITooltipFormatterParam[]) => {
    const p = Array.isArray(params) ? params[0] : params;
    const idx = (p as any).dataIndex;
    if (idx !== undefined && idx >= 0 && idx < htmlArray.length)
      return htmlArray[idx];
    return '';
  };
}

function applyEmitHighlight() {
  if (!chartInstance.value) return;
  const highlight = crossFilterStore.getHighlight(widgetId.value);
  if (!highlight) {
    chartInstance.value.dispatchAction({type: 'unselect'});
    return;
  }
  chartInstance.value.dispatchAction({
    type: 'select',
    seriesIndex: highlight.seriesIndex ?? 0,
    dataIndex: highlight.dataIndex,
  });
}

function createNumberFormatter(format: BIFormatSpec, isArabic: boolean): NumberFormatter {
  const locale = isArabic ? 'ar-SA' : 'en-US';
  const type = format.type || 'number';
  const decimals = format.decimals ?? 0;

  switch (type) {
    case 'currency': {
      const nf = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: format.currency || 'SAR',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        ...(format.compact ? {notation: 'compact' as const} : {})
      });
      return (v) => nf.format(v);
    }
    case 'percent': {
      const nf = new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: format.decimals ?? 1,
        maximumFractionDigits: format.decimals ?? 1,
      });
      return (v) => nf.format(v);
    }
    case 'compact': {
      const nf = new Intl.NumberFormat(locale, {
        notation: 'compact',
        minimumFractionDigits: format.decimals ?? 1,
        maximumFractionDigits: format.decimals ?? 1,
      });
      return (v) => nf.format(v);
    }
    default: {
      const nf = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: format.thousandsSeparator !== false,
        ...(format.compact ? {notation: 'compact' as const} : {})
      });
      return (v) => nf.format(v);
    }
  }
}

function createDefaultFormatter(isArabic: boolean): NumberFormatter {
  const locale = isArabic ? 'en-EG' : 'en-US';
  const nf = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    useGrouping: true,
  });
  return (v) => nf.format(v);
}

function applyFormatters(option: BIEChartsOption) {
  const seriesFormats = option._formatMeta?.seriesFormats || {};
  const isArabic = Translator.isArabic();

  const formatters: Record<number, NumberFormatter> = {};
  for (const [idx, fmt] of Object.entries(seriesFormats))
    formatters[Number(idx)] = createNumberFormatter(fmt, isArabic);

  const defaultFormatter = createDefaultFormatter(isArabic);

  injectTooltipFormatter(option, formatters, defaultFormatter);
  injectAxisFormatter(option, formatters, defaultFormatter);
  injectGaugeDetailFormatter(option, formatters, defaultFormatter);
  injectLabelFormatter(option, formatters, defaultFormatter);
}

function injectTooltipFormatter(
  option: BIEChartsOption,
  formatters: Record<number, NumberFormatter>,
  defaultFormatter: NumberFormatter,
) {
  const tooltip = option.tooltip as {trigger?: string; formatter?: unknown} | undefined;
  if (!tooltip) return;
  if (typeof tooltip.formatter === 'function' || typeof tooltip.formatter === 'string') return;
  const trigger = tooltip.trigger;

  const pickFormatter = (seriesIndex?: number) =>
    (seriesIndex !== undefined ? formatters[seriesIndex] : undefined) ?? defaultFormatter;

  if (trigger === 'axis') {
    tooltip.formatter = (params: BITooltipFormatterParam[]) => {
      let result = (params[0].axisValueLabel ?? '') + '<br/>';
      for (const p of params) {
        const fmt = pickFormatter(p.seriesIndex);
        const val = typeof p.value === 'number' ? fmt(p.value) : p.value;
        result += (p.marker ?? '') + ' ' + (p.seriesName ?? '') + ': ' + val + '<br/>';
      }
      return result;
    };
  } else if (trigger === 'item') {
    tooltip.formatter = (params: BITooltipFormatterParam) => {
      const fmt = pickFormatter(params.seriesIndex);
      const val = typeof params.value === 'number' ? fmt(params.value) : params.value;
      if (params.percent !== undefined)
        return (params.name ?? '') + ': ' + val + ' (' + params.percent + '%)';
      return (params.name ?? '') + ': ' + val;
    };
  }
}

function injectAxisFormatter(
  option: BIEChartsOption,
  formatters: Record<number, NumberFormatter>,
  defaultFormatter: NumberFormatter,
) {
  const firstFormatter = formatters[Number(Object.keys(formatters)[0])] ?? defaultFormatter;

  const applyToAxis = (axis: BIAxisOption | undefined) => {
    if (!axis || axis.type === 'category') return;
    if (!axis.axisLabel) axis.axisLabel = {};
    axis.axisLabel.formatter = (value: number) => firstFormatter(value);
  };

  const yAxis = option.yAxis as BIAxisOption | BIAxisOption[] | undefined;
  if (Array.isArray(yAxis))
    yAxis.forEach(applyToAxis);
  else
    applyToAxis(yAxis);

  const xAxis = option.xAxis as BIAxisOption | BIAxisOption[] | undefined;
  if (Array.isArray(xAxis))
    xAxis.filter((a) => a.type === 'value').forEach(applyToAxis);
  else if (xAxis?.type === 'value')
    applyToAxis(xAxis);
}

function injectGaugeDetailFormatter(
  option: BIEChartsOption,
  formatters: Record<number, NumberFormatter>,
  defaultFormatter: NumberFormatter,
) {
  const fmt = formatters[0] ?? defaultFormatter;
  const series = option.series as BIGaugeSeries[] | BIGaugeSeries | undefined;
  if (!Array.isArray(series)) return;
  for (const s of series) {
    if (s.type !== 'gauge' || !s.detail) continue;
    const existing = s.detail.formatter;
    // '{value}' is ECharts' no-op default; override it so our number formatter wins.
    if (typeof existing === 'function') continue;
    if (typeof existing === 'string' && existing !== '{value}') continue;
    s.detail.formatter = (value: number) => fmt(value);
  }
}

function injectLabelFormatter(
  option: BIEChartsOption,
  formatters: Record<number, NumberFormatter>,
  defaultFormatter: NumberFormatter,
) {
  const series = option.series as BISeriesWithLabel[] | BISeriesWithLabel | undefined;
  const seriesArr = Array.isArray(series) ? series : series ? [series] : [];
  for (let i = 0; i < seriesArr.length; i++) {
    const s = seriesArr[i];
    if (!s?.label?.show) continue;
    if (typeof s.label.formatter === 'function' || typeof s.label.formatter === 'string') continue;
    const fmt = formatters[i] ?? formatters[0] ?? defaultFormatter;
    s.label.formatter = (p: BILabelFormatterParam) => {
      const v = Array.isArray(p.value) ? p.value[p.value.length - 1] : p.value;
      return typeof v === 'number' ? fmt(v) : String(v ?? '');
    };
  }
}

const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());
const exportCopied = ref(false);

function openWidgetEditView() {
  const id = widgetId.value;
  if (!id) return;
  const link = PlaceUtils.editLink('DashBoardWidget', id);
  if (link) window.open(link, '_blank');
}

function copyServerResponse() {
  const line = biChartLine.value;
  if (!line) return;
  navigator.clipboard.writeText(JSON.stringify(line, null, 2));
}

function exportEChartOption() {
  const echartOption = getEchartOption();
  if (!echartOption) return;
  const clean = JSON.parse(JSON.stringify(echartOption));
  for (const key of Object.keys(clean)) {
    if (key.startsWith('_'))
      delete clean[key];
  }
  delete clean.toolbox;
  const text = 'option = ' + JSON.stringify(clean, null, 2) + ';\n';
  navigator.clipboard.writeText(text).then(() => {
    exportCopied.value = true;
    setTimeout(() => exportCopied.value = false, 2000);
  });
}
</script>

<template>
  <div class="tw-h-full tw-w-full tw-flex tw-flex-col tw-relative tw-rounded"
       :class="{
         'echart-widget-source': isFilterSource,
         'echart-widget-filtered': isFilteredByOther,
       }">
    <div v-if="isFilterSource || isFilteredByOther"
         class="tw-absolute tw-top-1 tw-end-1 tw-z-10 tw-flex tw-items-center tw-gap-1">
      <div v-if="isFilterSource"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-primary tw-text-white tw-shadow"
           :title="emittingTooltip">
        <q-icon name="filter_alt" size="14px" class="emit-funnel"/>
      </div>
      <div v-if="isFilteredByOther"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-info tw-text-white tw-shadow"
           :title="filteredByTooltip">
        <q-icon name="filter_alt" size="14px"/>
      </div>
    </div>
    <q-dialog v-model="runtimeSlotsDialogOpen">
      <q-card v-if="biChartLine.runtimeSelectorInfo && widgetId" :dark="$q.dark.isActive">
        <WizardSlotSelector :widget-id="widgetId"
                            :info="biChartLine.runtimeSelectorInfo"
                            @applied="onSlotOverrideChanged"
                            @reset="onSlotOverrideChanged"
                            @close="runtimeSlotsDialogOpen = false"/>
      </q-card>
    </q-dialog>
    <div v-if="biChartLine.arChartTitle || biChartLine.enChartTitle"
         class="tw-text-center tw-text-sm tw-font-semibold tw-py-1 tw-text-gray-700 dark:tw-text-gray-300 tw-shrink-0">
      {{ DashboardUtils.calcChartTitle(biChartLine) }}
    </div>
    <div v-if="errorMessage"
         class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded">
      <q-icon name="warning" color="orange" size="2rem"/>
      <p class="tw-text-sm tw-text-gray-600 dark:tw-text-gray-400 tw-mt-2 tw-text-center tw-px-4">{{ errorMessage }}</p>
    </div>
    <div v-else ref="chartRef" class="tw-h-full tw-w-full tw-grow"/>
    <BIActionMenu v-model="drillDownMenuOpen"
                  :targets="drillDownMenu?.targets ?? []"
                  :point-values="drillDownMenu?.pointValues ?? {}"
                  :filter-metadata="drillDownMenu?.filterMetadata ?? {}"
                  :drill-down-by-dimensions="drillDownMenu?.drillDownByDimensions ?? []"
                  :link-targets="drillDownMenu?.linkTargets ?? []"
                  :link-point-values="drillDownMenu?.linkPointValues ?? {}"
                  :source-label="drillDownMenu?.sourceLabel"
                  :x="drillDownMenu?.x ?? 0"
                  :y="drillDownMenu?.y ?? 0"
                  @drill-down-selected="selectDrillDownTarget"
                  @drill-down-by-selected="selectDrillDownByDimension"
                  @link-selected="selectLinkTarget"/>
  </div>
</template>

<style scoped>
.echart-widget-source {
  box-shadow: inset 0 0 0 2px var(--q-primary);
}

.echart-widget-filtered {
  box-shadow: inset 0 0 0 1px var(--q-info);
}

.echart-widget-source.echart-widget-filtered {
  box-shadow: inset 0 0 0 2px var(--q-primary), inset 0 0 0 4px var(--q-info);
}

.emit-funnel {
  transform: rotate(180deg);
}
</style>
